import PageAnimator from '../components/ui/PageAnimator';
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, MessageSquare, Send, CheckCircle2, AlertTriangle } from 'lucide-react';
import api from '../api';

export default function SupportContactForm() {
    const [formData, setFormData] = useState({
        email: '',
        subject: '',
        message: ''
    });
    const [showConfirm, setShowConfirm] = useState(false);
    const [notification, setNotification] = useState({ show: false, message: "", type: "success" });
    const [loading, setLoading] = useState(false);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const showToast = (message, type = "success") => {
        setNotification({ show: true, message, type });
        setTimeout(() => setNotification({ show: false, message: "", type: "success" }), 4000);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setShowConfirm(true);
    };

    const confirmSend = async () => {
        setShowConfirm(false);
        setLoading(true);

        try {
            await api.post("/send-email", formData);
            showToast("Votre demande a été envoyée avec succès ! Notre équipe vous répondra rapidement.", "success");
            setFormData({ email: '', subject: '', message: '' });
        } catch (error) {
            console.error("Erreur lors de l'envoi de l'email :", error);
            showToast("Une erreur s'est produite lors de l'envoi du message.", "error");
        } finally {
            setLoading(false);
        }
    };

    return (
    <div className="p-8 bg-workspace min-h-screen flex items-center justify-center pb-24">
      <PageAnimator>
            <div className="w-full max-w-2xl">
                
                <div className="text-center mb-10">
                    <motion.div 
                        initial={{ scale: 0 }} 
                        animate={{ scale: 1 }} 
                        className="w-16 h-16 bg-primary-100 text-primary-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-sm"
                    >
                        <MessageSquare className="w-8 h-8" />
                    </motion.div>
                    <h2 className="text-3xl font-bold text-primary mb-3">
                        Besoin d’aide ? Contactez-nous !
                    </h2>
                    <p className="text-muted text-lg">
                        Une question technique ou commerciale ? L’équipe support de <span className="font-bold text-primary">NANTHIAT</span> est à votre disposition.
                    </p>
                </div>

                <motion.form 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    onSubmit={handleSubmit} 
                    className="workspace-card-elevated p-8"
                >
                    <div className="space-y-6">
                        <div>
                            <label htmlFor="email" className="block text-sm font-semibold text-primary mb-2">
                                Adresse Email de contact
                            </label>
                            <div className="relative">
                                <Mail className="absolute left-4 top-3 h-5 w-5 text-muted" />
                                <input
                                    id="email"
                                    name="email"
                                    type="email"
                                    required
                                    value={formData.email}
                                    onChange={handleChange}
                                    placeholder="vous@exemple.com"
                                    className="block w-full workspace-input pl-12 py-3"
                                />
                            </div>
                        </div>

                        <div>
                            <label htmlFor="subject" className="block text-sm font-semibold text-primary mb-2">
                                Sujet de votre demande
                            </label>
                            <input
                                id="subject"
                                name="subject"
                                type="text"
                                required
                                value={formData.subject}
                                onChange={handleChange}
                                placeholder="Problème avec la facturation..."
                                className="block w-full workspace-input py-3 px-4"
                            />
                        </div>

                        <div>
                            <label htmlFor="message" className="block text-sm font-semibold text-primary mb-2">
                                Message détaillé
                            </label>
                            <textarea
                                id="message"
                                name="message"
                                rows={5}
                                required
                                value={formData.message}
                                onChange={handleChange}
                                placeholder="Décrivez votre besoin ici..."
                                className="block w-full workspace-input py-3 px-4 resize-none"
                            />
                        </div>
                    </div>

                    <div className="mt-8">
                        <button 
                            type="submit" 
                            disabled={loading}
                            className="btn btn-primary w-full py-3.5 text-base flex justify-center items-center gap-2 disabled:opacity-50"
                        >
                            <Send className="w-5 h-5" />
                            {loading ? "Envoi en cours..." : "Envoyer ma demande"}
                        </button>
                    </div>
                </motion.form>
            </div>

            {/* Modal de confirmation d'envoi (Framer Motion) */}
            <AnimatePresence>
                {showConfirm && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <motion.div 
                            initial={{ opacity: 0 }} 
                            animate={{ opacity: 1 }} 
                            exit={{ opacity: 0 }}
                            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
                            onClick={() => setShowConfirm(false)}
                        />
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            className="workspace-card-elevated p-6 max-w-sm w-full relative z-10"
                        >
                            <h3 className="text-lg font-bold text-primary mb-2">Confirmer l'envoi</h3>
                            <p className="text-muted text-sm mb-6">Êtes-vous sûr de vouloir envoyer cette demande au support technique ?</p>
                            <div className="flex gap-3 justify-end">
                                <button 
                                    onClick={() => setShowConfirm(false)} 
                                    className="px-4 py-2 rounded-lg border border-tertiary text-muted hover:bg-surface transition-colors text-sm font-medium"
                                >
                                    Annuler
                                </button>
                                <button 
                                    onClick={confirmSend} 
                                    className="btn btn-primary px-6 py-2 text-sm"
                                >
                                    Confirmer
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Toast Notification */}
            <AnimatePresence>
                {notification.show && (
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 50 }}
                        className="fixed bottom-6 right-6 z-[70] w-full max-w-sm"
                    >
                        <div className={`workspace-card-elevated p-4 flex items-start gap-3 border-l-4 ${notification.type === "success" ? "border-success-500 bg-success-50/50" : "border-danger-500 bg-danger-50/50"}`}>
                            <div className="shrink-0 mt-0.5">
                                {notification.type === "success" ? <CheckCircle2 className="h-5 w-5 text-success-500" /> : <AlertTriangle className="h-5 w-5 text-danger-500" />}
                            </div>
                            <div className="flex-1 text-sm text-primary">
                                <p className="font-bold">{notification.type === "success" ? "Succès" : "Erreur"}</p>
                                <p className="text-subtle mt-1">{notification.message}</p>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </PageAnimator>
    </div>
    );}
